import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { useSelector } from "react-redux";
import moment from "moment";
import dayjs from "dayjs";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import {
  designColors,
  designShell,
  designType,
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { GlobalConfirmDialog, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";
import { RequestStatusCards } from "../RequestStatusCards";
import {
  cancelOwnClaim,
  fetchMyClaims,
  fromApiClaim,
  useActiveReimbursementTypes,
} from "pages/PayRoll/Shared/reimbursementApi";
import {
  ClaimFootnote,
  ClaimRequestPanel,
  ClaimStatusChip,
  ClaimViewPanel,
  PaidInCell,
  formatDay,
  plainAmount,
  toListRow,
} from "pages/PayRoll/Shared/reimbursementClaimUi";

// My Workspace > My Requests > Reimbursement. The employee's own
// reimbursement claims: status cards, the standard list toolbar, a New
// Request panel with "Before you submit" checks and a view panel with the
// claim's progress up to "Paid in salary".
// Data: GET /api/expense/?selected_user_id=<me> (own claims), POST
// /api/expense/ (submit), PUT /api/expense/{id}/ (cancel while Pending), see
// pages/PayRoll/Shared/reimbursementApi.js. The Business Advance tab was
// removed on request.

const HEADERS = [
  { name: "Request ID", key: "request_id", type: "string", icon: <ConfirmationNumberOutlinedIcon fontSize="small" /> },
  { name: "Reimbursement Type", key: "type_name", type: "autocomplete", icon: <CategoryOutlinedIcon fontSize="small" /> },
  { name: "Purpose / Description", key: "purpose", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
  { name: "Amount (₹)", key: "amount", type: "number", icon: <PaidOutlinedIcon fontSize="small" /> },
  { name: "Bill Date", key: "bill_date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { name: "Status", key: "status_label", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { name: "Paid In", key: "paid_in", type: "autocomplete", icon: <AccountBalanceOutlinedIcon fontSize="small" /> },
  { name: "Submitted On", key: "submitted_on", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
];

const STATUS_KEYS = ["All", "Pending", "Approved", "Paid", "Rejected", "Cancelled"];
const NUMERIC_KEYS = new Set(["amount"]);

const headCellSx = { ...designShell.tableHeadCellBase, whiteSpace: "nowrap" };
const bodyCellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell };

const renderCell = (row, key) => {
  switch (key) {
    case "amount":
      return plainAmount(row.amount);
    case "bill_date":
    case "submitted_on":
      return <Mui.Box component="span" sx={{ whiteSpace: "nowrap" }}>{formatDay(row[key])}</Mui.Box>;
    case "status_label":
      return <ClaimStatusChip status={row.status} />;
    case "paid_in":
      return <PaidInCell row={row} />;
    case "purpose":
      return (
        <Mui.Box sx={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.purpose}>
          {row.purpose}
        </Mui.Box>
      );
    default:
      return row[key] ?? "—";
  }
};

// Phone layout: one card per claim.
const ClaimMobileCard = ({ row, headers, onClick }) => (
  <Mui.Box
    onClick={onClick}
    sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: 1.5, p: 1.5, bgcolor: designColors.white, cursor: "pointer" }}
  >
    <Mui.Stack direction="row" justifyContent="space-between" spacing={1.5}>
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: 600 }}>{row.type_name}</Mui.Typography>
        <Mui.Typography sx={designType.captionText}>{row.request_id}</Mui.Typography>
      </Mui.Box>
      <ClaimStatusChip status={row.status} />
    </Mui.Stack>
    <Mui.Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${designColors.slate200}`, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
      {headers
        .filter((head) => !["type_name", "request_id", "status_label"].includes(head.key))
        .map((head) => (
          <Mui.Box key={head.key} sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ ...designType.captionText, fontWeight: 600 }}>{head.name}</Mui.Typography>
            <Mui.Box sx={{ ...designType.tableBodyCell, mt: 0.25, minWidth: 0 }}>{renderCell(row, head.key)}</Mui.Box>
          </Mui.Box>
        ))}
    </Mui.Box>
  </Mui.Box>
);

const ReimbursementClaimsContent = ({ subModuleId }) => {
  const { domain, userDetails, userScreenAccess, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Same editor check the old Expense Claims screen used ("Expense Claims"
  // submodule under My Request).
  const checkAccess = checkEditorPermission(userScreenAccess?.module_details, "My Request", "Expense Claims");

  // Types map the claim's type name to its id and payslip line.
  const { types } = useActiveReimbursementTypes(domain, topbarCompanyId);
  const [apiClaims, setApiClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  // Full-page loader only until the first list arrives; reloads after a
  // submit / cancel keep the list on screen.
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!domain || !userDetails?.id) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    fetchMyClaims({ domain, companyId: topbarCompanyId, userId: userDetails.id, signal: controller.signal })
      .then((rows) => { if (!controller.signal.aborted) setApiClaims(rows); })
      .catch((error) => { if (!controller.signal.aborted) setLoadError(error.message); })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setLoadedOnce(true);
      });
    return () => controller.abort();
  }, [domain, topbarCompanyId, userDetails?.id, reload]);

  const claims = useMemo(() => apiClaims.map((row) => fromApiClaim(row, { self: true, types })), [apiClaims, types]);
  const [statusFilter, setStatusFilter] = useState("");
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  // Opens on Last 30 Days (filters on the submitted date).
  const [range, setRange] = useState(() => ({
    startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    label: "Last 30 Days",
  }));
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const applicant = {
    id: userDetails?.id,
    name: [userDetails?.first_name, userDetails?.last_name].filter(Boolean).join(" ") || userDetails?.username || "",
    code: userDetails?.employee_id,
    site: userDetails?.department_name,
  };

  const rangeRows = useMemo(
    () =>
      claims
        .map(toListRow)
        .filter((row) => row.submitted_on >= range.startDate && row.submitted_on <= range.endDate),
    [claims, range],
  );

  const counts = useMemo(
    () => rangeRows.reduce((acc, row) => ({ ...acc, [row.status]: (acc[row.status] || 0) + 1 }), {}),
    [rangeRows],
  );

  const filterInputRows = useMemo(
    () => rangeRows.filter((row) => !statusFilter || row.status === statusFilter),
    [rangeRows, statusFilter],
  );

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_reimbursement_saved_views",
    fullHeaders: HEADERS,
    screenAccess: true,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name": userDetails?.username || userDetails?.user_name || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  const viewRow = viewId ? claims.find((row) => row.id === viewId) || null : null;
  const canCancel = checkAccess && viewRow?.status === "Pending";

  const handleCancel = async () => {
    if (!viewRow || cancelling) return;
    setCancelling(true);
    try {
      const updated = await cancelOwnClaim({ domain, companyId: topbarCompanyId, id: viewRow.id });
      setApiClaims((prev) => prev.map((row) => (row.id === viewRow.id ? { ...row, ...(updated || { status: "Cancelled" }) } : row)));
      setCancelOpen(false);
      showToast("success", `${viewRow.requestId} cancelled.`);
    } catch (error) {
      showToast("error", error.message || "Couldn't cancel the claim. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading && !loadedOnce) return <Loader />;

  return (
    <Mui.Box>
      <Mui.Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Mui.Box>
          <Mui.Typography component="h1" sx={designType.pageTitle}>Reimbursement Claims</Mui.Typography>
          <Mui.Typography sx={designType.pageSubtitle}>
            Claim money you spent from your own pocket for work. Approved claims are paid with your salary.
          </Mui.Typography>
        </Mui.Box>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }} sx={{ width: { xs: "100%", md: "auto" } }}>
          <ApprovalDateRangeFilter
            startDate={range.startDate}
            endDate={range.endDate}
            dateRangeLabel={range.label}
            onChange={(start, end, label) => {
              setRange({
                startDate: moment(start).format("YYYY-MM-DD"),
                endDate: moment(end).format("YYYY-MM-DD"),
                label: label || "Custom Range",
              });
              setPage(0);
            }}
            includeCurrentDate
            showToday
          />
          {checkAccess ? (
            <PrimaryButton
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
              onClick={() => setIsPanelOpen(true)}
            >
              New Request
            </PrimaryButton>
          ) : null}
        </Mui.Stack>
      </Mui.Stack>

      <RequestStatusCards
        keys={STATUS_KEYS}
        labels={{ Paid: "Paid in salary" }}
        counts={counts}
        total={rangeRows.length}
        activeStatus={statusFilter}
        onSelect={(status) => {
          setStatusFilter(status);
          setPage(0);
        }}
      />

      <Mui.Box sx={{ ...designShell.sectionCard, p: { xs: 1.5, sm: 2 } }}>
        {loadError ? (
          <Mui.Alert
            severity="error"
            sx={{ mb: 1.5 }}
            action={<Mui.Button color="inherit" onClick={() => setReload((value) => value + 1)}>Retry</Mui.Button>}
          >
            {loadError}
          </Mui.Alert>
        ) : null}
        <CustomFilter
          title="Reimbursement Claims"
          titleOverride={titleOverride}
          filterHeaders={visibleHeaders}
          data={filterInputRows}
          filteredData={tableRows}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
          showSaveButton={true}
          hideCount={true}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={true}
          metadata={reportMetadata}
          labeledToolbar={true}
          searchPlaceholder="Search by claim ID, type or purpose"
        />

        <Mui.Box sx={{ display: { xs: "grid", sm: "none" }, gap: 1.25, mt: 1.5 }}>
          {paginatedRows.length === 0 ? (
            <Mui.Box sx={{ py: 4, textAlign: "center", color: designColors.slate600, border: `1px dashed ${designColors.slate200}`, borderRadius: 1.5 }}>
              No reimbursement claims found.
            </Mui.Box>
          ) : (
            paginatedRows.map((row) => (
              <ClaimMobileCard key={row.id} row={row} headers={visibleHeaders} onClick={() => setViewId(row.id)} />
            ))
          )}
        </Mui.Box>

        <Mui.TableContainer sx={{ overflowX: "auto", display: { xs: "none", sm: "block" }, mt: 1.5 }}>
          <Mui.Table size="small" sx={{ minWidth: 1040 }}>
            <Mui.TableHead>
              <Mui.TableRow>
                {visibleHeaders.map((head) => (
                  <Mui.TableCell key={head.key} sx={{ ...headCellSx, ...(NUMERIC_KEYS.has(head.key) ? { textAlign: "right" } : {}) }}>
                    {head.name}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {paginatedRows.length === 0 ? (
                <TableEmptyState
                  colSpan={visibleHeaders.length || 1}
                  dataLength={paginatedRows}
                  title="No Reimbursement Claims Found"
                  subtitle="Use New Request to claim money you spent for work."
                />
              ) : (
                paginatedRows.map((row) => (
                  <Mui.TableRow
                    key={row.id}
                    hover
                    onClick={() => setViewId(row.id)}
                    sx={{ cursor: "pointer", ...(viewId === row.id ? designShell.neutralSelectedRow : {}) }}
                  >
                    {visibleHeaders.map((head) => (
                      <Mui.TableCell key={head.key} sx={{ ...bodyCellSx, ...(NUMERIC_KEYS.has(head.key) ? { textAlign: "right" } : {}) }}>
                        {renderCell(row, head.key)}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>

        {tableRows.some((row) => row.status === "Pending") ? <ClaimFootnote /> : null}

        <Mui.Box sx={{ mt: 1, pt: 1.5, borderTop: `1px solid ${designColors.slate200}` }}>
          {paginationFooter({
            page,
            rowsPerPage,
            total: tableRows.length,
            itemLabel: "claims",
            onPageChange: setPage,
            onRowsPerPageChange: (event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            },
            rowsPerPageOptions,
          })}
        </Mui.Box>
      </Mui.Box>

      <ClaimRequestPanel
        open={isPanelOpen || Boolean(editRow)}
        onClose={() => {
          setIsPanelOpen(false);
          setEditRow(null);
        }}
        editRow={editRow}
        mode="self"
        domain={domain}
        companyId={topbarCompanyId}
        applicant={applicant}
        claims={claims}
        onSubmitted={() => {
          setReload((value) => value + 1);
          setStatusFilter("");
          setRange({
            startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
            endDate: dayjs().format("YYYY-MM-DD"),
            label: "Last 30 Days",
          });
        }}
      />

      <ClaimViewPanel
        open={Boolean(viewRow)}
        row={viewRow}
        employee={{
          name: viewRow?.employee?.name || applicant.name,
          code: viewRow?.employee?.code || applicant.code,
          department: viewRow?.employee?.department || userDetails?.department_name,
          designation: viewRow?.employee?.designation || userDetails?.designation,
          photo: viewRow?.employee?.photo || userDetails?.image_url,
        }}
        types={types}
        domain={domain}
        companyId={topbarCompanyId}
        onClose={() => setViewId(null)}
        headerActions={
          canCancel ? (
            <PanelHeaderIconButton
              title="Edit reimbursement request"
              onClick={() => {
                setEditRow(viewRow);
                setViewId(null);
              }}
            >
              <EditIcon />
            </PanelHeaderIconButton>
          ) : null
        }
        footer={
          // Only Cancel, like the other My Workspace view panels; the panel
          // closes from its header.
          canCancel ? <RequestCancelButton onClick={() => setCancelOpen(true)}>Cancel claim</RequestCancelButton> : null
        }
      />

      <GlobalConfirmDialog
        open={cancelOpen}
        title="Cancel this claim?"
        confirmLabel="Cancel claim"
        cancelLabel="Keep claim"
        danger
        onCancel={() => { if (!cancelling) setCancelOpen(false); }}
        onConfirm={handleCancel}
      >
        {viewRow ? `${viewRow.requestId} (${viewRow.typeName}, ₹${plainAmount(viewRow.amount)}) will be cancelled and not paid.` : null}
      </GlobalConfirmDialog>
    </Mui.Box>
  );
};

export const ExpenseClaim = ({ subModuleId }) => <ReimbursementClaimsContent subModuleId={subModuleId} />;

export default ExpenseClaim;
